import {
  DeviceSDKError,
  __privateAdd,
  __privateGet,
  __privateMethod,
  __privateSet,
  __privateWrapper,
  eventemitter3_default,
  mapServerError
} from "./chunk-VB4U6RYK.mjs";

// src/enrollment-service.ts
import { Configuration, EnrollmentApi } from "@envoy/devices_client";
var DefaultEnrollmentService = class {
  constructor(baseURL) {
    this.api = new EnrollmentApi(
      new Configuration({
        basePath: baseURL,
        baseOptions: { withCredentials: true }
      })
    );
  }
  async enroll(product, telemetry, signal, clientBindingToken) {
    try {
      const response = await this.api.enrollDevice(
        {
          enrollDeviceRequest: {
            product,
            telemetry: telemetry ?? void 0,
            ...clientBindingToken ? { client_binding_token: clientBindingToken } : {}
          }
        },
        { signal }
      );
      return response.data;
    } catch (err) {
      if (signal.aborted) {
        throw new DeviceSDKError("Enrollment cancelled", "ENROLLMENT_FAILED", false, {
          cause: err
        });
      }
      if (isAxiosError(err) && err.response?.data) {
        const data = err.response.data;
        if (data.error_code) {
          throw mapServerError(data.error_code, data.message ?? "", err);
        }
      }
      if (isAxiosError(err) && !err.response) {
        throw new DeviceSDKError("Network error during enrollment", "NETWORK_ERROR", true, {
          cause: err
        });
      }
      if (isAxiosError(err) && err.response?.status !== void 0) {
        const status = err.response.status;
        if (status >= 500 || status === 429) {
          throw new DeviceSDKError(
            `Server error during enrollment (HTTP ${status})`,
            "UNEXPECTED_RESPONSE",
            true,
            { cause: err }
          );
        }
      }
      throw new DeviceSDKError("Unexpected error during enrollment", "UNEXPECTED_RESPONSE", false, {
        cause: err
      });
    }
  }
};
function isAxiosError(err) {
  return typeof err === "object" && err !== null && "isAxiosError" in err;
}

// src/mqtt-client-wrapper.ts
import * as _awsIotSdk from "aws-iot-device-sdk-v2";
var awsIotSdk = _awsIotSdk;
var AUTH_FAILURE_REASON_CODES = /* @__PURE__ */ new Set([
  134,
  // 0x86 — Bad Username or Password
  135
  // 0x87 — Not Authorized
]);
var DefaultMqttClientFactory = class {
  create(enrollment, events) {
    return new AwsIotMqttClientWrapper(enrollment, events);
  }
};
var _client, _mqtt5, _decoder, _closed;
var AwsIotMqttClientWrapper = class {
  constructor(enrollment, events) {
    __privateAdd(this, _client);
    // aws-iot-device-sdk-v2 Mqtt5Client — typed as any to avoid Node.js type imports
    __privateAdd(this, _mqtt5);
    // cached module reference
    __privateAdd(this, _decoder, new TextDecoder());
    __privateAdd(this, _closed, false);
    const { iot, mqtt5 } = awsIotSdk;
    __privateSet(this, _mqtt5, mqtt5);
    const builder = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithCustomAuth(
      enrollment.iot_endpoint,
      {
        authorizerName: enrollment.authorizer_name,
        username: enrollment.client_id,
        password: enrollment.iot_jwt
      }
    );
    builder.withConnectProperties({
      clientId: enrollment.client_id,
      keepAliveIntervalSeconds: enrollment.keep_alive_seconds,
      sessionExpiryIntervalSeconds: enrollment.session_expiry_seconds
    });
    builder.withMinReconnectDelayMs(1e3);
    builder.withMaxReconnectDelayMs(6e4);
    builder.withMinConnectedTimeToResetReconnectDelayMs(6e4);
    builder.withRetryJitterMode(mqtt5.RetryJitterType.Full);
    builder.withSessionBehavior(mqtt5.ClientSessionBehavior.RejoinPostSuccess);
    const config = builder.build();
    __privateSet(this, _client, new mqtt5.Mqtt5Client(config));
    if (events.onAttemptingConnect) {
      __privateGet(this, _client).on("attemptingConnect", events.onAttemptingConnect);
    }
    if (events.onConnectionSuccess) {
      __privateGet(this, _client).on("connectionSuccess", (event) => {
        events.onConnectionSuccess({
          sessionPresent: event.connack?.sessionPresent ?? false
        });
      });
    }
    if (events.onConnectionFailure) {
      __privateGet(this, _client).on("connectionFailure", (event) => {
        events.onConnectionFailure({
          reasonCode: event.connack?.reasonCode,
          error: event.error
        });
      });
    }
    if (events.onDisconnection) {
      __privateGet(this, _client).on("disconnection", (event) => {
        events.onDisconnection({
          reasonCode: event.disconnect?.reasonCode,
          error: event.error
        });
      });
    }
    if (events.onStopped) {
      __privateGet(this, _client).on("stopped", events.onStopped);
    }
    if (events.onMessageReceived) {
      __privateGet(this, _client).on("messageReceived", (event) => {
        const topic = event.message?.topicName ?? "";
        const payload = event.message?.payload ? __privateGet(this, _decoder).decode(event.message.payload) : "";
        events.onMessageReceived({ topic, payload });
      });
    }
  }
  start() {
    __privateGet(this, _client).start();
  }
  stop() {
    __privateGet(this, _client).stop();
  }
  async subscribe(topics) {
    if (topics.length === 0) return;
    const subscriptions = topics.map((topic) => ({
      topicFilter: topic,
      qos: __privateGet(this, _mqtt5).QoS.AtLeastOnce
    }));
    await __privateGet(this, _client).subscribe({ subscriptions });
  }
  close() {
    if (__privateGet(this, _closed)) return;
    __privateSet(this, _closed, true);
    __privateGet(this, _client).removeAllListeners();
    __privateGet(this, _client).close();
  }
};
_client = new WeakMap();
_mqtt5 = new WeakMap();
_decoder = new WeakMap();
_closed = new WeakMap();

// src/visibility-observer.ts
var _events, _visibilityHandler, _onlineHandler, _offlineHandler;
var DefaultVisibilityObserver = class {
  constructor(events) {
    __privateAdd(this, _events);
    __privateAdd(this, _visibilityHandler, null);
    __privateAdd(this, _onlineHandler, null);
    __privateAdd(this, _offlineHandler, null);
    __privateSet(this, _events, events);
  }
  start() {
    if (typeof document !== "undefined") {
      __privateSet(this, _visibilityHandler, () => {
        if (document.visibilityState === "visible") {
          __privateGet(this, _events).onVisible?.();
        }
      });
      document.addEventListener("visibilitychange", __privateGet(this, _visibilityHandler));
    }
    if (typeof window !== "undefined") {
      if (__privateGet(this, _events).onOnline) {
        __privateSet(this, _onlineHandler, () => __privateGet(this, _events).onOnline());
        window.addEventListener("online", __privateGet(this, _onlineHandler));
      }
      if (__privateGet(this, _events).onOffline) {
        __privateSet(this, _offlineHandler, () => __privateGet(this, _events).onOffline());
        window.addEventListener("offline", __privateGet(this, _offlineHandler));
      }
    }
  }
  stop() {
    if (__privateGet(this, _visibilityHandler) && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", __privateGet(this, _visibilityHandler));
      __privateSet(this, _visibilityHandler, null);
    }
    if (__privateGet(this, _onlineHandler) && typeof window !== "undefined") {
      window.removeEventListener("online", __privateGet(this, _onlineHandler));
      __privateSet(this, _onlineHandler, null);
    }
    if (__privateGet(this, _offlineHandler) && typeof window !== "undefined") {
      window.removeEventListener("offline", __privateGet(this, _offlineHandler));
      __privateSet(this, _offlineHandler, null);
    }
  }
};
_events = new WeakMap();
_visibilityHandler = new WeakMap();
_onlineHandler = new WeakMap();
_offlineHandler = new WeakMap();

// src/message-parser.ts
function parseMessage(payload) {
  let data;
  try {
    data = JSON.parse(payload);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return null;
  }
  let timestamp;
  if (typeof data.timestamp === "number") {
    timestamp = new Date(data.timestamp);
  } else {
    timestamp = /* @__PURE__ */ new Date();
  }
  return {
    id: typeof data.id === "string" ? data.id : "",
    type: typeof data.type === "string" ? data.type : "",
    timestamp,
    source: typeof data.source === "string" ? data.source : "",
    payload: typeof data.payload === "object" && data.payload !== null && !Array.isArray(data.payload) ? data.payload : {}
  };
}

// src/EnvoyDeviceSDK.ts
var REDACTED_KEYS = /* @__PURE__ */ new Set(["iot_jwt", "password", "token", "accessToken"]);
var BACKOFF_BASE_MS = 1e3;
var MAX_BACKOFF_MS = 6e4;
var EVICTION_WINDOW_MS = 1e4;
var EVICTION_THRESHOLD = 3;
var DEDUP_CLEAR_INTERVAL_MS = 6e5;
var DEDUP_MAX_ENTRIES = 1e3;
var VISIBILITY_DEBOUNCE_MS = 500;
var DEFAULT_CONSOLE_LOG_PREFIX = "[EnvoyDeviceSDK]";
var MAX_TIMEOUT_MS = 2147483647;
var _nextInstanceId, _config, _enrollmentService, _mqttClientFactory, _visibilityObserver, _createVisibilityObserver, _instanceId, _state, _enrollment, _clientBindingToken, _clientBindingTokenStorage, _mqttClient, _subscribedTopics, _nextMqttClientId, _activeMqttClientId, _currentOp, _abortController, _evictionTimestamps, _isOffline, _reconnectAttempt, _stopping, _onStoppedResolve, _refreshAtMs, _reEnrollmentTimer, _reEnrollmentGeneration, _reEnrollmentInFlight, _reEnrollAbort, _expectingEviction, _handoffOldClient, _handoffOldClientId, _dedupSet, _dedupClearTimer, _visibilityDebounceTimer, _EnvoyDeviceSDK_instances, setClientBindingToken_fn, readClientBindingTokenFromStorage_fn, writeClientBindingTokenToStorage_fn, runExclusive_fn, enrollAndConnect_fn, connectMqtt_fn, handleMqttAuthFailure_fn, subscribeToTopics_fn, disconnectMqtt_fn, scheduleReEnrollment_fn, armReEnrollmentTimer_fn, clearReEnrollmentTimer_fn, triggerReEnrollment_fn, performReEnrollment_fn, getTelemetry_fn, makeBeforeBreakHandoff_fn, cleanupHandoffOldClient_fn, cancelHandoff_fn, startDedupTimer_fn, clearDedupTimer_fn, isDuplicate_fn, recordEviction_fn, handleVisibilityVisible_fn, clearVisibilityDebounce_fn, shouldReEnroll_fn, handleNetworkOnline_fn, handleNetworkOffline_fn, _VALID_TRANSITIONS, transition_fn, log_fn, sanitizeLogAttributes_fn, mirrorLogToConsole_fn, getConsoleMethod_fn, delay_fn;
var _EnvoyDeviceSDK = class _EnvoyDeviceSDK extends eventemitter3_default {
  constructor(config, deps) {
    super();
    __privateAdd(this, _EnvoyDeviceSDK_instances);
    __privateAdd(this, _config);
    __privateAdd(this, _enrollmentService);
    __privateAdd(this, _mqttClientFactory);
    __privateAdd(this, _visibilityObserver, null);
    __privateAdd(this, _createVisibilityObserver);
    __privateAdd(this, _instanceId, __privateWrapper(_EnvoyDeviceSDK, _nextInstanceId)._++);
    __privateAdd(this, _state, { type: "disconnected" });
    __privateAdd(this, _enrollment, null);
    __privateAdd(this, _clientBindingToken, null);
    __privateAdd(this, _clientBindingTokenStorage);
    // MQTT client
    __privateAdd(this, _mqttClient, null);
    __privateAdd(this, _subscribedTopics, []);
    __privateAdd(this, _nextMqttClientId, 1);
    __privateAdd(this, _activeMqttClientId, null);
    // Operation guard (inline) — serializes start/stop calls
    __privateAdd(this, _currentOp, null);
    __privateAdd(this, _abortController, null);
    // Multi-tab eviction detection
    __privateAdd(this, _evictionTimestamps, []);
    // Network state
    __privateAdd(this, _isOffline, false);
    // Reconnection attempt counter
    __privateAdd(this, _reconnectAttempt, 0);
    // Track if we are stopping (to distinguish expected vs unexpected disconnect)
    __privateAdd(this, _stopping, false);
    // Callback to resolve when MQTT client fires 'stopped' event
    __privateAdd(this, _onStoppedResolve, null);
    // Re-enrollment timer
    __privateAdd(this, _refreshAtMs, null);
    // cached: when to refresh the JWT (epoch ms)
    __privateAdd(this, _reEnrollmentTimer, null);
    __privateAdd(this, _reEnrollmentGeneration, 0);
    __privateAdd(this, _reEnrollmentInFlight, null);
    __privateAdd(this, _reEnrollAbort, null);
    // Make-before-break handoff
    __privateAdd(this, _expectingEviction, false);
    __privateAdd(this, _handoffOldClient, null);
    __privateAdd(this, _handoffOldClientId, null);
    // Message deduplication (shared across handoffs, reset on stop+start)
    __privateAdd(this, _dedupSet, /* @__PURE__ */ new Set());
    __privateAdd(this, _dedupClearTimer, null);
    // Visibility debounce
    __privateAdd(this, _visibilityDebounceTimer, null);
    __privateSet(this, _config, config);
    __privateSet(this, _enrollmentService, deps?.enrollmentService ?? new DefaultEnrollmentService(config.baseURL));
    __privateSet(this, _mqttClientFactory, deps?.mqttClientFactory ?? new DefaultMqttClientFactory());
    __privateSet(this, _clientBindingTokenStorage, config.clientBindingTokenStorage ?? null);
    __privateSet(this, _clientBindingToken, __privateMethod(this, _EnvoyDeviceSDK_instances, readClientBindingTokenFromStorage_fn).call(this));
    if (deps?.visibilityObserver) {
      __privateSet(this, _createVisibilityObserver, () => deps.visibilityObserver);
    } else {
      __privateSet(this, _createVisibilityObserver, () => new DefaultVisibilityObserver({
        onVisible: () => __privateMethod(this, _EnvoyDeviceSDK_instances, handleVisibilityVisible_fn).call(this),
        onOnline: () => __privateMethod(this, _EnvoyDeviceSDK_instances, handleNetworkOnline_fn).call(this),
        onOffline: () => __privateMethod(this, _EnvoyDeviceSDK_instances, handleNetworkOffline_fn).call(this)
      }));
    }
  }
  /** Current connection state (synchronous read). */
  get currentState() {
    return __privateGet(this, _state);
  }
  /** Device ID after successful enrollment, null otherwise. */
  get deviceId() {
    return __privateGet(this, _enrollment)?.device_id ?? null;
  }
  /**
   * Start the SDK: enroll the device and connect via MQTT.
   * Resolves after enrollment succeeds. MQTT connection progress
   * is communicated via `stateChange` events.
   * No-op if already enrolling/connecting/connected.
   */
  async start() {
    if (__privateGet(this, _state).type === "enrolling" || __privateGet(this, _state).type === "connecting" || __privateGet(this, _state).type === "connected" || __privateGet(this, _state).type === "reconnecting") {
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "start() called while already active, ignoring");
      return;
    }
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Starting SDK");
    return __privateMethod(this, _EnvoyDeviceSDK_instances, runExclusive_fn).call(this, async (signal) => {
      await __privateMethod(this, _EnvoyDeviceSDK_instances, enrollAndConnect_fn).call(this, signal);
    });
  }
  /**
   * Stop the SDK: cancel in-flight operations, disconnect MQTT, and clean up.
   */
  async stop() {
    if (__privateGet(this, _state).type === "disconnected") {
      return;
    }
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Stopping SDK");
    __privateSet(this, _stopping, true);
    __privateMethod(this, _EnvoyDeviceSDK_instances, clearReEnrollmentTimer_fn).call(this);
    __privateGet(this, _reEnrollAbort)?.abort();
    __privateSet(this, _reEnrollAbort, null);
    __privateMethod(this, _EnvoyDeviceSDK_instances, clearVisibilityDebounce_fn).call(this);
    if (__privateGet(this, _reEnrollmentInFlight)) {
      await __privateGet(this, _reEnrollmentInFlight).catch(() => {
      });
    }
    __privateSet(this, _reEnrollmentInFlight, null);
    __privateGet(this, _abortController)?.abort();
    if (__privateGet(this, _currentOp)) {
      await __privateGet(this, _currentOp).catch(() => {
      });
    }
    __privateGet(this, _visibilityObserver)?.stop();
    __privateSet(this, _visibilityObserver, null);
    if (__privateGet(this, _handoffOldClient)) {
      const old = __privateGet(this, _handoffOldClient);
      const oldMqttClientId = __privateGet(this, _handoffOldClientId);
      __privateSet(this, _handoffOldClient, null);
      __privateSet(this, _handoffOldClientId, null);
      __privateSet(this, _expectingEviction, false);
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Stopping handoff MQTT client during stop()", {
        mqtt_client_id: oldMqttClientId
      });
      old.stop();
      queueMicrotask(() => old.close());
    }
    await __privateMethod(this, _EnvoyDeviceSDK_instances, disconnectMqtt_fn).call(this);
    __privateGet(this, _dedupSet).clear();
    __privateMethod(this, _EnvoyDeviceSDK_instances, clearDedupTimer_fn).call(this);
    __privateSet(this, _enrollment, null);
    __privateSet(this, _refreshAtMs, null);
    __privateSet(this, _subscribedTopics, []);
    __privateSet(this, _reconnectAttempt, 0);
    __privateSet(this, _evictionTimestamps, []);
    __privateSet(this, _isOffline, false);
    __privateSet(this, _stopping, false);
    __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "disconnected" });
  }
  /**
   * Forget the cached `client_binding_token` (in-memory and in storage).
   * Call when the device unpairs so the next enrollment mints a fresh MQTT identity
   * instead of re-binding to the previous device's broker `client_id`.
   */
  forgetIdentity() {
    __privateMethod(this, _EnvoyDeviceSDK_instances, setClientBindingToken_fn).call(this, null);
  }
};
_nextInstanceId = new WeakMap();
_config = new WeakMap();
_enrollmentService = new WeakMap();
_mqttClientFactory = new WeakMap();
_visibilityObserver = new WeakMap();
_createVisibilityObserver = new WeakMap();
_instanceId = new WeakMap();
_state = new WeakMap();
_enrollment = new WeakMap();
_clientBindingToken = new WeakMap();
_clientBindingTokenStorage = new WeakMap();
_mqttClient = new WeakMap();
_subscribedTopics = new WeakMap();
_nextMqttClientId = new WeakMap();
_activeMqttClientId = new WeakMap();
_currentOp = new WeakMap();
_abortController = new WeakMap();
_evictionTimestamps = new WeakMap();
_isOffline = new WeakMap();
_reconnectAttempt = new WeakMap();
_stopping = new WeakMap();
_onStoppedResolve = new WeakMap();
_refreshAtMs = new WeakMap();
_reEnrollmentTimer = new WeakMap();
_reEnrollmentGeneration = new WeakMap();
_reEnrollmentInFlight = new WeakMap();
_reEnrollAbort = new WeakMap();
_expectingEviction = new WeakMap();
_handoffOldClient = new WeakMap();
_handoffOldClientId = new WeakMap();
_dedupSet = new WeakMap();
_dedupClearTimer = new WeakMap();
_visibilityDebounceTimer = new WeakMap();
_EnvoyDeviceSDK_instances = new WeakSet();
// ---------------------------------------------------------------------------
// Client binding token persistence
// ---------------------------------------------------------------------------
setClientBindingToken_fn = function(token) {
  __privateSet(this, _clientBindingToken, token);
  __privateMethod(this, _EnvoyDeviceSDK_instances, writeClientBindingTokenToStorage_fn).call(this, token);
};
readClientBindingTokenFromStorage_fn = function() {
  if (!__privateGet(this, _clientBindingTokenStorage)) return null;
  try {
    return __privateGet(this, _clientBindingTokenStorage).get();
  } catch (err) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "clientBindingTokenStorage.get() threw, ignoring cached token", {
      error: String(err)
    });
    return null;
  }
};
writeClientBindingTokenToStorage_fn = function(token) {
  if (!__privateGet(this, _clientBindingTokenStorage)) return;
  try {
    __privateGet(this, _clientBindingTokenStorage).set(token);
  } catch (err) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "clientBindingTokenStorage.set() threw, token not persisted", {
      error: String(err)
    });
  }
};
runExclusive_fn = async function(op) {
  __privateGet(this, _abortController)?.abort();
  if (__privateGet(this, _currentOp)) {
    await __privateGet(this, _currentOp).catch(() => {
    });
  }
  const ac = new AbortController();
  __privateSet(this, _abortController, ac);
  const p = op(ac.signal).finally(() => {
    if (__privateGet(this, _abortController) === ac) {
      __privateSet(this, _abortController, null);
      __privateSet(this, _currentOp, null);
    }
  });
  __privateSet(this, _currentOp, p);
  return p;
};
enrollAndConnect_fn = async function(signal) {
  __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "enrolling" });
  for (let attempt = 0; ; attempt++) {
    if (signal.aborted) {
      __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "disconnected" });
      return;
    }
    try {
      const telemetry = await __privateMethod(this, _EnvoyDeviceSDK_instances, getTelemetry_fn).call(this);
      if (signal.aborted) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "disconnected" });
        return;
      }
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", `Enrolling device (attempt ${attempt + 1})`);
      const result = await __privateGet(this, _enrollmentService).enroll(
        __privateGet(this, _config).product,
        telemetry,
        signal,
        __privateGet(this, _clientBindingToken)
      );
      if (signal.aborted) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "disconnected" });
        return;
      }
      __privateSet(this, _enrollment, result);
      __privateMethod(this, _EnvoyDeviceSDK_instances, setClientBindingToken_fn).call(this, result.client_binding_token);
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Enrollment successful", {
        device_id: result.device_id,
        iot_endpoint: result.iot_endpoint
      });
      __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "connecting" });
      __privateMethod(this, _EnvoyDeviceSDK_instances, connectMqtt_fn).call(this, result, "initial_enrollment");
      __privateSet(this, _visibilityObserver, __privateGet(this, _createVisibilityObserver).call(this));
      __privateGet(this, _visibilityObserver).start();
      __privateMethod(this, _EnvoyDeviceSDK_instances, startDedupTimer_fn).call(this);
      return;
    } catch (err) {
      if (signal.aborted) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "disconnected" });
        return;
      }
      if (err instanceof DeviceSDKError) {
        if (!err.isRetryable) {
          __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", `Enrollment failed (non-retryable): ${err.code}`, {
            serverErrorCode: err.serverErrorCode
          });
          __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "error", error: err });
          if (err.code === "AUTHORIZATION_FAILURE") {
            this.emit("authorizationFailure", err);
          }
          return;
        }
        const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, attempt), MAX_BACKOFF_MS) * (0.5 + Math.random() * 0.5);
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", `Enrollment failed (retryable), retrying in ${Math.round(delay)}ms`, {
          serverErrorCode: err.serverErrorCode,
          attempt: attempt + 1
        });
        await __privateMethod(this, _EnvoyDeviceSDK_instances, delay_fn).call(this, delay, signal);
        continue;
      }
      const sdkError = new DeviceSDKError(
        "Unexpected enrollment error",
        "ENROLLMENT_FAILED",
        false,
        { cause: err }
      );
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", `Enrollment failed unexpectedly: ${String(err)}`);
      __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "error", error: sdkError });
      return;
    }
  }
};
// ---------------------------------------------------------------------------
// MQTT connection
// ---------------------------------------------------------------------------
connectMqtt_fn = function(enrollment, reason) {
  const mqttClientId = __privateWrapper(this, _nextMqttClientId)._++;
  const mqttLogAttributes = {
    mqtt_client_id: mqttClientId,
    mqtt_connection_reason: reason
  };
  __privateSet(this, _activeMqttClientId, mqttClientId);
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Connecting to MQTT", {
    iot_endpoint: enrollment.iot_endpoint,
    client_id: enrollment.client_id,
    ...mqttLogAttributes
  });
  __privateSet(this, _subscribedTopics, enrollment.topics.subscribe);
  __privateSet(this, _mqttClient, __privateGet(this, _mqttClientFactory).create(enrollment, {
    onAttemptingConnect: () => {
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "MQTT attempting connection", mqttLogAttributes);
    },
    onConnectionSuccess: (event) => {
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "MQTT connected", {
        sessionPresent: event.sessionPresent,
        ...mqttLogAttributes
      });
      __privateSet(this, _reconnectAttempt, 0);
      if (__privateGet(this, _handoffOldClient)) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, cleanupHandoffOldClient_fn).call(this);
      }
      if (!event.sessionPresent) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, subscribeToTopics_fn).call(this, __privateGet(this, _subscribedTopics));
      }
      __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "connected" });
      __privateMethod(this, _EnvoyDeviceSDK_instances, scheduleReEnrollment_fn).call(this, enrollment);
    },
    onConnectionFailure: (event) => {
      if (__privateGet(this, _handoffOldClient)) {
        if (event.reasonCode !== void 0 && AUTH_FAILURE_REASON_CODES.has(event.reasonCode)) {
          __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", `Handoff: new MQTT auth failure (reason code ${event.reasonCode}), keeping old connection`, mqttLogAttributes);
          __privateMethod(this, _EnvoyDeviceSDK_instances, cancelHandoff_fn).call(this);
          this.emit(
            "authorizationFailure",
            new DeviceSDKError(
              `Handoff MQTT auth failure (reason code ${event.reasonCode})`,
              "AUTHORIZATION_FAILURE",
              false
            )
          );
          return;
        }
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "Handoff: new MQTT client connection attempt failed, awaiting retry", {
          reasonCode: event.reasonCode,
          ...mqttLogAttributes
        });
        return;
      }
      if (__privateMethod(this, _EnvoyDeviceSDK_instances, handleMqttAuthFailure_fn).call(this, event.reasonCode, "MQTT auth failure")) {
        return;
      }
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "MQTT connection failed", {
        reasonCode: event.reasonCode,
        error: event.error?.message,
        offline: __privateGet(this, _isOffline),
        ...mqttLogAttributes
      });
      if (__privateGet(this, _state).type !== "error") {
        __privateWrapper(this, _reconnectAttempt)._++;
        __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "reconnecting", attempt: __privateGet(this, _reconnectAttempt) });
      }
    },
    onDisconnection: (event) => {
      if (__privateGet(this, _stopping)) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "MQTT disconnection expected during stop()", {
          reasonCode: event.reasonCode,
          error: event.error?.message,
          ...mqttLogAttributes
        });
        return;
      }
      if (__privateGet(this, _expectingEviction)) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "MQTT disconnection suppressed during handoff", {
          reasonCode: event.reasonCode,
          error: event.error?.message,
          handoff_role: __privateGet(this, _handoffOldClientId) === mqttClientId ? "old" : "new",
          ...mqttLogAttributes
        });
        return;
      }
      if (__privateMethod(this, _EnvoyDeviceSDK_instances, handleMqttAuthFailure_fn).call(this, event.reasonCode, "MQTT disconnected due to auth failure")) {
        return;
      }
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "MQTT disconnected unexpectedly", {
        reasonCode: event.reasonCode,
        error: event.error?.message,
        ...mqttLogAttributes
      });
      __privateMethod(this, _EnvoyDeviceSDK_instances, recordEviction_fn).call(this);
      __privateWrapper(this, _reconnectAttempt)._++;
      __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "reconnecting", attempt: __privateGet(this, _reconnectAttempt) });
    },
    onStopped: () => {
      var _a;
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "MQTT client stopped", mqttLogAttributes);
      (_a = __privateGet(this, _onStoppedResolve)) == null ? void 0 : _a.call(this);
      __privateSet(this, _onStoppedResolve, null);
    },
    onMessageReceived: (event) => {
      const msg = parseMessage(event.payload);
      if (!msg) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "Malformed MQTT message \u2014 invalid JSON", { topic: event.topic });
        return;
      }
      if (msg.id && __privateMethod(this, _EnvoyDeviceSDK_instances, isDuplicate_fn).call(this, msg.id)) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "Duplicate message filtered", {
          message_id: msg.id,
          topic: event.topic
        });
        return;
      }
      this.emit("message", msg);
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Message delivered", {
        message_id: msg.id,
        type: msg.type,
        source: msg.source
      });
    }
  }));
  __privateGet(this, _mqttClient).start();
};
/** Returns true if the reason code indicates auth failure and was handled. */
handleMqttAuthFailure_fn = function(reasonCode, logPrefix) {
  if (reasonCode === void 0 || !AUTH_FAILURE_REASON_CODES.has(reasonCode)) {
    return false;
  }
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", `${logPrefix} (reason code ${reasonCode})`);
  const error = new DeviceSDKError(
    `MQTT authorization failed (reason code ${reasonCode})`,
    "AUTHORIZATION_FAILURE",
    false
  );
  __privateMethod(this, _EnvoyDeviceSDK_instances, transition_fn).call(this, { type: "error", error });
  this.emit("authorizationFailure", error);
  __privateGet(this, _mqttClient)?.stop();
  return true;
};
subscribeToTopics_fn = async function(topics) {
  if (topics.length === 0) return;
  try {
    await __privateGet(this, _mqttClient)?.subscribe(topics);
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Subscribed to topics", { topics });
  } catch (err) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", "Failed to subscribe to topics", {
      topics,
      error: String(err)
    });
  }
};
disconnectMqtt_fn = async function() {
  if (!__privateGet(this, _mqttClient)) return;
  const client = __privateGet(this, _mqttClient);
  const mqttClientId = __privateGet(this, _activeMqttClientId);
  __privateSet(this, _mqttClient, null);
  __privateSet(this, _activeMqttClientId, null);
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Stopping MQTT client", {
    mqtt_client_id: mqttClientId
  });
  const stoppedPromise = new Promise((resolve) => {
    const timeout = setTimeout(resolve, 5e3);
    __privateSet(this, _onStoppedResolve, () => {
      clearTimeout(timeout);
      resolve();
    });
  });
  client.stop();
  await stoppedPromise;
  client.close();
};
// ---------------------------------------------------------------------------
// Re-enrollment timer
// ---------------------------------------------------------------------------
scheduleReEnrollment_fn = function(enrollment, minDelayMs = 0) {
  __privateMethod(this, _EnvoyDeviceSDK_instances, clearReEnrollmentTimer_fn).call(this);
  const expiresAt = new Date(enrollment.iot_jwt_expires_at).getTime();
  const refreshAt = expiresAt - enrollment.refresh_margin_seconds * 1e3;
  const effectiveAt = Math.max(refreshAt, Date.now() + minDelayMs);
  __privateSet(this, _refreshAtMs, effectiveAt);
  const delayMs = Math.max(0, effectiveAt - Date.now());
  const generation = ++__privateWrapper(this, _reEnrollmentGeneration)._;
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", `Re-enrollment scheduled in ${Math.round(delayMs / 1e3)}s`);
  __privateMethod(this, _EnvoyDeviceSDK_instances, armReEnrollmentTimer_fn).call(this, generation);
};
armReEnrollmentTimer_fn = function(generation) {
  const refreshAt = __privateGet(this, _refreshAtMs);
  if (refreshAt === null) return;
  const delayMs = Math.max(0, refreshAt - Date.now());
  const timeoutMs = Math.min(delayMs, MAX_TIMEOUT_MS);
  __privateSet(this, _reEnrollmentTimer, setTimeout(() => {
    if (__privateGet(this, _reEnrollmentGeneration) !== generation) return;
    if (__privateMethod(this, _EnvoyDeviceSDK_instances, shouldReEnroll_fn).call(this)) {
      void __privateMethod(this, _EnvoyDeviceSDK_instances, triggerReEnrollment_fn).call(this, "timer");
      return;
    }
    __privateMethod(this, _EnvoyDeviceSDK_instances, armReEnrollmentTimer_fn).call(this, generation);
  }, timeoutMs));
};
clearReEnrollmentTimer_fn = function() {
  if (__privateGet(this, _reEnrollmentTimer) !== null) {
    clearTimeout(__privateGet(this, _reEnrollmentTimer));
    __privateSet(this, _reEnrollmentTimer, null);
  }
};
triggerReEnrollment_fn = async function(trigger) {
  if (__privateGet(this, _state).type !== "connected") {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", `Re-enrollment skipped (state: ${__privateGet(this, _state).type})`);
    return;
  }
  if (__privateGet(this, _reEnrollmentInFlight)) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", `Re-enrollment coalesced (trigger: ${trigger})`);
    await __privateGet(this, _reEnrollmentInFlight);
    return;
  }
  __privateMethod(this, _EnvoyDeviceSDK_instances, clearReEnrollmentTimer_fn).call(this);
  ++__privateWrapper(this, _reEnrollmentGeneration)._;
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", `Re-enrollment triggered by ${trigger}`);
  __privateSet(this, _reEnrollmentInFlight, __privateMethod(this, _EnvoyDeviceSDK_instances, performReEnrollment_fn).call(this).finally(() => {
    __privateSet(this, _reEnrollmentInFlight, null);
  }));
  await __privateGet(this, _reEnrollmentInFlight);
};
performReEnrollment_fn = async function() {
  const ac = new AbortController();
  __privateSet(this, _reEnrollAbort, ac);
  const signal = ac.signal;
  for (let attempt = 0; ; attempt++) {
    if (__privateGet(this, _stopping) || signal.aborted) return;
    try {
      const telemetry = await __privateMethod(this, _EnvoyDeviceSDK_instances, getTelemetry_fn).call(this);
      if (__privateGet(this, _stopping) || signal.aborted) return;
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", `Re-enrolling device (attempt ${attempt + 1})`);
      const result = await __privateGet(this, _enrollmentService).enroll(
        __privateGet(this, _config).product,
        telemetry,
        signal,
        __privateGet(this, _clientBindingToken)
      );
      if (__privateGet(this, _stopping) || signal.aborted) return;
      __privateSet(this, _enrollment, result);
      __privateMethod(this, _EnvoyDeviceSDK_instances, setClientBindingToken_fn).call(this, result.client_binding_token);
      __privateMethod(this, _EnvoyDeviceSDK_instances, makeBeforeBreakHandoff_fn).call(this, result);
      return;
    } catch (err) {
      if (__privateGet(this, _stopping) || signal.aborted) return;
      if (err instanceof DeviceSDKError) {
        if (err.code === "AUTHORIZATION_FAILURE") {
          __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", "Re-enrollment auth failure (cookie may have expired)");
          this.emit("authorizationFailure", err);
          return;
        }
        if (err.isRetryable) {
          const delay = Math.min(BACKOFF_BASE_MS * Math.pow(2, attempt), MAX_BACKOFF_MS) * (0.5 + Math.random() * 0.5);
          __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", `Re-enrollment failed (retryable), retrying in ${Math.round(delay)}ms`);
          await __privateMethod(this, _EnvoyDeviceSDK_instances, delay_fn).call(this, delay, signal);
          continue;
        }
      }
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "error", `Re-enrollment failed, preserving existing connection: ${String(err)}`);
      if (__privateGet(this, _enrollment)) {
        __privateMethod(this, _EnvoyDeviceSDK_instances, scheduleReEnrollment_fn).call(this, __privateGet(this, _enrollment), MAX_BACKOFF_MS);
      }
      return;
    }
  }
};
getTelemetry_fn = async function() {
  try {
    return await __privateGet(this, _config).telemetryProvider?.() ?? null;
  } catch (telemetryErr) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "telemetryProvider threw, proceeding without telemetry", {
      error: String(telemetryErr)
    });
    return null;
  }
};
// ---------------------------------------------------------------------------
// Make-before-break handoff
// ---------------------------------------------------------------------------
makeBeforeBreakHandoff_fn = function(newEnrollment) {
  if (!__privateGet(this, _mqttClient)) return;
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Starting make-before-break handoff", {
    mqtt_client_id: __privateGet(this, _activeMqttClientId)
  });
  __privateSet(this, _expectingEviction, true);
  __privateSet(this, _handoffOldClient, __privateGet(this, _mqttClient));
  __privateSet(this, _handoffOldClientId, __privateGet(this, _activeMqttClientId));
  __privateMethod(this, _EnvoyDeviceSDK_instances, connectMqtt_fn).call(this, newEnrollment, "re_enrollment_handoff");
};
cleanupHandoffOldClient_fn = function() {
  const old = __privateGet(this, _handoffOldClient);
  if (!old) return;
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Handoff complete, cleaning up old MQTT client", {
    previous_mqtt_client_id: __privateGet(this, _handoffOldClientId),
    active_mqtt_client_id: __privateGet(this, _activeMqttClientId)
  });
  __privateSet(this, _handoffOldClient, null);
  __privateSet(this, _handoffOldClientId, null);
  __privateSet(this, _expectingEviction, false);
  old.stop();
  queueMicrotask(() => old.close());
};
cancelHandoff_fn = function() {
  if (!__privateGet(this, _handoffOldClient)) return;
  const failedMqttClientId = __privateGet(this, _activeMqttClientId);
  const restoredMqttClientId = __privateGet(this, _handoffOldClientId);
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Handoff cancelled, restoring old connection", {
    failed_mqtt_client_id: failedMqttClientId,
    restored_mqtt_client_id: restoredMqttClientId
  });
  const failedNew = __privateGet(this, _mqttClient);
  __privateSet(this, _mqttClient, __privateGet(this, _handoffOldClient));
  __privateSet(this, _activeMqttClientId, __privateGet(this, _handoffOldClientId));
  __privateSet(this, _handoffOldClient, null);
  __privateSet(this, _handoffOldClientId, null);
  __privateSet(this, _expectingEviction, false);
  if (failedNew) {
    failedNew.stop();
    queueMicrotask(() => failedNew.close());
  }
};
// ---------------------------------------------------------------------------
// Message deduplication
// ---------------------------------------------------------------------------
startDedupTimer_fn = function() {
  __privateMethod(this, _EnvoyDeviceSDK_instances, clearDedupTimer_fn).call(this);
  __privateSet(this, _dedupClearTimer, setInterval(() => {
    __privateGet(this, _dedupSet).clear();
  }, DEDUP_CLEAR_INTERVAL_MS));
};
clearDedupTimer_fn = function() {
  if (__privateGet(this, _dedupClearTimer) !== null) {
    clearInterval(__privateGet(this, _dedupClearTimer));
    __privateSet(this, _dedupClearTimer, null);
  }
};
isDuplicate_fn = function(messageId) {
  if (__privateGet(this, _dedupSet).has(messageId)) return true;
  if (__privateGet(this, _dedupSet).size >= DEDUP_MAX_ENTRIES) {
    __privateGet(this, _dedupSet).clear();
  }
  __privateGet(this, _dedupSet).add(messageId);
  return false;
};
// ---------------------------------------------------------------------------
// Multi-tab eviction detection
// ---------------------------------------------------------------------------
recordEviction_fn = function() {
  const now = Date.now();
  __privateGet(this, _evictionTimestamps).push(now);
  const cutoff = now - EVICTION_WINDOW_MS;
  __privateSet(this, _evictionTimestamps, __privateGet(this, _evictionTimestamps).filter((t) => t > cutoff));
  if (__privateGet(this, _evictionTimestamps).length >= EVICTION_THRESHOLD) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", "Rapid disconnect/reconnect cycling detected \u2014 possible multi-tab conflict. Only one browser tab should maintain an active SDK connection.");
    __privateSet(this, _evictionTimestamps, []);
  }
};
// ---------------------------------------------------------------------------
// Visibility / network handlers
// ---------------------------------------------------------------------------
handleVisibilityVisible_fn = function() {
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", "Tab became visible");
  if (__privateGet(this, _state).type !== "connected") return;
  __privateMethod(this, _EnvoyDeviceSDK_instances, clearVisibilityDebounce_fn).call(this);
  __privateSet(this, _visibilityDebounceTimer, setTimeout(() => {
    __privateSet(this, _visibilityDebounceTimer, null);
    if (__privateGet(this, _state).type !== "connected") return;
    if (__privateMethod(this, _EnvoyDeviceSDK_instances, shouldReEnroll_fn).call(this)) {
      __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "IoT JWT past refresh time after tab visible, triggering re-enrollment");
      void __privateMethod(this, _EnvoyDeviceSDK_instances, triggerReEnrollment_fn).call(this, "visibility");
    }
  }, VISIBILITY_DEBOUNCE_MS));
};
clearVisibilityDebounce_fn = function() {
  if (__privateGet(this, _visibilityDebounceTimer) !== null) {
    clearTimeout(__privateGet(this, _visibilityDebounceTimer));
    __privateSet(this, _visibilityDebounceTimer, null);
  }
};
/** Check if re-enrollment should happen (JWT past refresh time or expired). */
shouldReEnroll_fn = function() {
  return __privateGet(this, _refreshAtMs) !== null && Date.now() >= __privateGet(this, _refreshAtMs);
};
handleNetworkOnline_fn = function() {
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Network came back online");
  __privateSet(this, _isOffline, false);
};
handleNetworkOffline_fn = function() {
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "info", "Network went offline");
  __privateSet(this, _isOffline, true);
};
_VALID_TRANSITIONS = new WeakMap();
transition_fn = function(next) {
  const from = __privateGet(this, _state).type;
  const to = next.type;
  if (from === to && to !== "reconnecting") {
    return;
  }
  const allowed = __privateGet(_EnvoyDeviceSDK, _VALID_TRANSITIONS)[from];
  if (!allowed?.has(to)) {
    __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "warn", `Invalid state transition: ${from} \u2192 ${to}`);
    return;
  }
  __privateSet(this, _state, next);
  this.emit("stateChange", next);
  __privateMethod(this, _EnvoyDeviceSDK_instances, log_fn).call(this, "debug", `State: ${from} \u2192 ${to}`);
};
// ---------------------------------------------------------------------------
// Logging (with JWT redaction)
// ---------------------------------------------------------------------------
log_fn = function(level, message, attributes) {
  const sanitized = __privateMethod(this, _EnvoyDeviceSDK_instances, sanitizeLogAttributes_fn).call(this, {
    ...attributes,
    sdk_instance_id: __privateGet(this, _instanceId)
  });
  const entry = { level, message, attributes: sanitized };
  this.emit("log", entry);
  __privateMethod(this, _EnvoyDeviceSDK_instances, mirrorLogToConsole_fn).call(this, entry);
};
sanitizeLogAttributes_fn = function(attrs) {
  const result = {};
  for (const [key, value] of Object.entries(attrs)) {
    result[key] = REDACTED_KEYS.has(key) ? "[REDACTED]" : value;
  }
  return result;
};
mirrorLogToConsole_fn = function(entry) {
  const consoleLogging = __privateGet(this, _config).consoleLogging;
  if (!consoleLogging) return;
  const prefix = typeof consoleLogging === "object" ? consoleLogging.prefix ?? DEFAULT_CONSOLE_LOG_PREFIX : DEFAULT_CONSOLE_LOG_PREFIX;
  const consoleMethod = __privateMethod(this, _EnvoyDeviceSDK_instances, getConsoleMethod_fn).call(this, entry.level);
  if (entry.attributes && Object.keys(entry.attributes).length > 0) {
    consoleMethod(prefix, entry.message, entry.attributes);
    return;
  }
  consoleMethod(prefix, entry.message);
};
getConsoleMethod_fn = function(level) {
  switch (level) {
    case "debug":
      return console.debug.bind(console);
    case "info":
      return console.info.bind(console);
    case "warn":
      return console.warn.bind(console);
    case "error":
      return console.error.bind(console);
  }
};
// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
delay_fn = function(ms, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
};
__privateAdd(_EnvoyDeviceSDK, _nextInstanceId, 1);
// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------
/** Valid state transitions (from → allowed next states). */
__privateAdd(_EnvoyDeviceSDK, _VALID_TRANSITIONS, {
  disconnected: /* @__PURE__ */ new Set(["enrolling"]),
  enrolling: /* @__PURE__ */ new Set(["connecting", "error", "disconnected"]),
  connecting: /* @__PURE__ */ new Set(["connected", "reconnecting", "error", "disconnected"]),
  connected: /* @__PURE__ */ new Set(["reconnecting", "error", "disconnected", "enrolling"]),
  reconnecting: /* @__PURE__ */ new Set(["connected", "reconnecting", "error", "disconnected"]),
  error: /* @__PURE__ */ new Set(["enrolling", "disconnected"])
});
var EnvoyDeviceSDK = _EnvoyDeviceSDK;
export {
  AUTH_FAILURE_REASON_CODES,
  DeviceSDKError,
  EnvoyDeviceSDK,
  mapServerError
};
